import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, RefreshCw, Youtube, Rss, EyeOff, Eye, Check, X, AlertTriangle, Pencil, Search } from "lucide-react";
import api, { streamThumb } from "../lib/api";

const parseList = (s) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

const srcStatus = (s) => {
  if (s.last_error) return { color: "#EF4444", label: `Error: ${s.last_error}` };
  if (!s.last_ingested_at) return { color: "#D4AF37", label: "Not yet pulled" };
  const ageH = (Date.now() - new Date(s.last_ingested_at).getTime()) / 3.6e6;
  if (ageH > 24) return { color: "#D4AF37", label: `Stale: last pulled ${fmt(s.last_ingested_at)}` };
  return { color: "#14A89A", label: `Healthy: pulled ${fmt(s.last_ingested_at)}` };
};

const SubTab = ({ id, label, active, onClick }) => (
  <button onClick={() => onClick(id)} data-testid={`stream-admin-tab-${id}`} className={`pb-2 font-body text-xs tracking-wider uppercase transition-colors border-b-2 ${active === id ? "text-white border-[#14A89A]" : "text-zinc-500 hover:text-white border-transparent"}`}>{label}</button>
);

const Sources = () => {
  const [sources, setSources] = useState(null);
  const [type, setType] = useState("youtube");
  const [handle, setHandle] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [name, setName] = useState("");
  const [topics, setTopics] = useState("");
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [topicDraft, setTopicDraft] = useState("");
  const [q, setQ] = useState("");
  const [toast, setToast] = useState("");
  const [retrying, setRetrying] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const load = async () => { const { data } = await api.get("/admin/stream/sources"); setSources(data); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    setAddErr(""); setAdding(true);
    const payload = { type, name: name.trim(), topics: parseList(topics), active: true };
    if (type === "youtube") payload.handle = handle.trim();
    else payload.feed_url = feedUrl.trim();
    try {
      const { data } = await api.post("/admin/stream/sources", payload);
      setHandle(""); setFeedUrl(""); setName(""); setTopics("");
      await load();
      notify(`Added "${data.name || "source"}", pulling items now…`);
      setTimeout(load, 3000);
    } catch (e) {
      setAddErr(e?.response?.data?.detail || "Could not add. Check the handle / feed URL.");
    }
    setAdding(false);
  };

  const toggleActive = async (s) => {
    await api.patch(`/admin/stream/sources/${s.id}`, { active: !s.active });
    load();
  };
  const del = async (s) => {
    if (!window.confirm(`Remove "${s.name}"? This deletes its ingested items too.`)) return;
    await api.delete(`/admin/stream/sources/${s.id}`);
    load();
  };
  const saveTopics = async (s) => {
    await api.patch(`/admin/stream/sources/${s.id}`, { topics: parseList(topicDraft) });
    setEditing(null); load();
  };
  const reingest = async () => {
    setIngesting(true);
    try { await api.post("/admin/stream/ingest"); notify("Re-ingestion started, new items appear within a minute."); }
    finally { setIngesting(false); }
  };

  const retry = async (s) => {
    setRetrying(s.id);
    try {
      const { data } = await api.post(`/admin/stream/sources/${s.id}/ingest`);
      notify(data.last_error ? `Still erroring: ${data.last_error}` : `Pulled ${data.ingested} new item(s) from "${s.name || "source"}".`);
      load();
    } catch { notify("Retry failed. Please try again."); }
    setRetrying(null);
  };

  const ql = q.trim().toLowerCase();
  const filtered = (sources || []).filter((s) => !ql ||
    [s.name, s.handle, s.feed_url, ...(s.topics || [])].filter(Boolean).join(" ").toLowerCase().includes(ql));

  return (
    <div className="space-y-8" data-testid="stream-admin-sources">
      {/* Add form */}
      <div className="border border-white/10 p-5">
        <h3 className="font-heading text-sm text-white mb-4">Add a source</h3>
        <div className="flex gap-2 mb-3">
          {["youtube", "rss"].map((t) => (
            <button key={t} onClick={() => setType(t)} data-testid={`stream-source-type-${t}`} className={`flex items-center gap-2 px-4 py-2 font-body text-xs tracking-wider uppercase border transition-colors ${type === t ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
              {t === "youtube" ? <Youtube size={14} /> : <Rss size={14} />} {t === "youtube" ? "YouTube" : "RSS / Article"}
            </button>
          ))}
        </div>
        {type === "youtube" ? (
          <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@ChannelHandle (e.g. @QuantaScienceChannel)" data-testid="stream-source-handle" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body mb-2" />
        ) : (
          <input value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} placeholder="https://example.com/feed.xml" data-testid="stream-source-feed" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body mb-2" />
        )}
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name (optional for YouTube)" data-testid="stream-source-name" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body mb-2" />
        <input value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="Topics, comma-separated (e.g. Consciousness, Philosophy)" data-testid="stream-source-topics" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body mb-3" />
        {addErr && <p className="font-body text-xs text-red-400 mb-3 flex items-center gap-1.5"><AlertTriangle size={13} /> {addErr}</p>}
        <button onClick={add} disabled={adding || (type === "youtube" ? !handle.trim() : !feedUrl.trim())} data-testid="stream-source-add" className="bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-2 font-body text-sm disabled:opacity-40">
          {adding ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Add & ingest
        </button>
      </div>

      {/* List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-sm text-white">Sources {sources && <span className="text-zinc-600">· {sources.length}</span>}</h3>
          <button onClick={reingest} disabled={ingesting} data-testid="stream-reingest" className="border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs tracking-wider uppercase hover:bg-white/5 transition-colors flex items-center gap-2 disabled:opacity-40">
            <RefreshCw size={13} className={ingesting ? "animate-spin" : ""} /> Re-ingest all
          </button>
        </div>
        {sources && sources.length > 8 && (
          <div className="flex items-center gap-2 border border-white/15 px-3 mb-4 focus-within:border-[#14A89A] transition-colors">
            <Search size={15} className="text-zinc-500 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter sources by name, handle or topic" data-testid="stream-source-search" className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none font-body" />
            {q && <button onClick={() => setQ("")} data-testid="stream-source-search-clear" className="text-zinc-500 hover:text-white shrink-0"><X size={15} /></button>}
          </div>
        )}
        {sources === null ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
        ) : filtered.length === 0 ? (
          <p className="font-body text-sm text-zinc-600 py-6">{q ? `No sources match "${q}".` : "No sources yet. Add one above."}</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((s) => { const st = srcStatus(s); return (
              <div key={s.id} data-testid={`stream-source-${s.id}`} className={`border p-4 ${s.active ? "border-white/10" : "border-white/5 opacity-60"}`}>
                <div className="flex items-start gap-3">
                  {s.type === "youtube" ? <Youtube size={16} className="text-[#14A89A] shrink-0 mt-0.5" /> : <Rss size={16} className="text-[#A855F7] shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.color }} title={st.label} data-testid={`stream-source-status-${s.id}`} />
                      <div className="font-heading text-sm text-white truncate">{s.name || s.handle || s.feed_url}</div>
                    </div>
                    <div className="font-body text-[11px] text-zinc-500 truncate">{s.handle || s.feed_url}</div>
                    {editing === s.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <input value={topicDraft} onChange={(e) => setTopicDraft(e.target.value)} data-testid={`stream-source-topic-input-${s.id}`} className="flex-1 bg-transparent border-b border-white/15 px-0 py-1 text-xs text-white focus:outline-none focus:border-[#14A89A] font-body" />
                        <button onClick={() => saveTopics(s)} data-testid={`stream-source-topic-save-${s.id}`} className="text-[#14A89A]"><Check size={15} /></button>
                        <button onClick={() => setEditing(null)} className="text-zinc-500"><X size={15} /></button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {(s.topics || []).map((t) => <span key={t} className="font-body text-[10px] text-zinc-400 border border-white/10 px-2 py-0.5">{t}</span>)}
                        <button onClick={() => { setEditing(s.id); setTopicDraft((s.topics || []).join(", ")); }} data-testid={`stream-source-edit-topics-${s.id}`} className="text-zinc-600 hover:text-[#14A89A] ml-1"><Pencil size={12} /></button>
                      </div>
                    )}
                    {s.last_error && <p className="font-body text-[10px] text-red-400 mt-1.5 flex items-center gap-1"><AlertTriangle size={11} /> {s.last_error}</p>}
                    {s.last_ingested_at && !s.last_error && <p className="font-body text-[10px] text-zinc-600 mt-1.5">Last pulled {fmt(s.last_ingested_at)}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {s.last_error && (
                      <button onClick={() => retry(s)} disabled={retrying === s.id} data-testid={`stream-source-retry-${s.id}`} title="Retry ingest for this source" className="font-body text-[10px] tracking-wider uppercase px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-1 disabled:opacity-40">
                        <RefreshCw size={11} className={retrying === s.id ? "animate-spin" : ""} /> Retry
                      </button>
                    )}
                    <button onClick={() => toggleActive(s)} data-testid={`stream-source-toggle-${s.id}`} title={s.active ? "Active: click to pause" : "Paused: click to activate"} className={`font-body text-[10px] tracking-wider uppercase px-2 py-1 border transition-colors ${s.active ? "border-[#14A89A]/40 text-[#14A89A]" : "border-white/15 text-zinc-500"}`}>
                      {s.active ? "Active" : "Paused"}
                    </button>
                    <button onClick={() => del(s)} data-testid={`stream-source-delete-${s.id}`} className="text-zinc-600 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>

      {toast && (
        <div data-testid="stream-toast" className="fixed bottom-6 right-6 z-[200] bg-[#0D0D0D] border border-[#14A89A]/40 px-4 py-3 flex items-center gap-2.5 fade-up shadow-lg">
          <Check size={15} className="text-[#14A89A] shrink-0" />
          <span className="font-body text-sm text-white">{toast}</span>
        </div>
      )}
    </div>
  );
};

const Filters = () => {
  const [ch, setCh] = useState("");
  const [kw, setKw] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/stream/config").then(({ data }) => {
      setCh((data.exclude_channels || []).join("\n"));
      setKw((data.exclude_keywords || []).join("\n"));
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/stream/config", { exclude_channels: parseList(ch), exclude_keywords: parseList(kw) });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="stream-admin-filters">
      <p className="font-body text-sm text-zinc-400 leading-relaxed">Items whose author matches an excluded channel, or whose title contains an excluded keyword, are filtered out during ingestion. One entry per line.</p>
      <div>
        <label className="font-body text-[11px] tracking-wider uppercase text-zinc-500 mb-2 block">Exclude channels / authors</label>
        <textarea value={ch} onChange={(e) => setCh(e.target.value)} rows={5} data-testid="stream-exclude-channels" placeholder={"Bashar\nAubrey Marcus"} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none" />
      </div>
      <div>
        <label className="font-body text-[11px] tracking-wider uppercase text-zinc-500 mb-2 block">Exclude title keywords</label>
        <textarea value={kw} onChange={(e) => setKw(e.target.value)} rows={5} data-testid="stream-exclude-keywords" placeholder={"giveaway\nsponsored"} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none" />
      </div>
      <button onClick={save} disabled={saving} data-testid="stream-filters-save" className="bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-2 font-body text-sm disabled:opacity-40">
        {saving ? <Loader2 className="animate-spin" size={15} /> : saved ? <Check size={15} /> : null} {saved ? "Saved" : "Save filters"}
      </button>
    </div>
  );
};

const Items = () => {
  const [kind, setKind] = useState("video");
  const [items, setItems] = useState(null);
  const PAGE = 24;

  const load = async () => {
    setItems(null);
    const { data } = await api.get("/admin/stream/items", { params: { kind, limit: PAGE } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const toggleHide = async (it) => {
    setItems((xs) => xs.map((x) => x.id === it.id ? { ...x, hidden: !x.hidden } : x));
    try { await api.post(`/admin/stream/items/${it.id}/hide`, { hidden: !it.hidden }); }
    catch { setItems((xs) => xs.map((x) => x.id === it.id ? { ...x, hidden: it.hidden } : x)); }
  };

  return (
    <div data-testid="stream-admin-items">
      <div className="flex gap-2 mb-6">
        {["video", "article"].map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`stream-items-kind-${k}`} className={`px-4 py-2 font-body text-xs tracking-wider uppercase border transition-colors ${kind === k ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>{k === "video" ? "Videos" : "Articles"}</button>
        ))}
      </div>
      {items === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
      ) : items.length === 0 ? (
        <p className="font-body text-sm text-zinc-600 py-6">No items yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} data-testid={`stream-item-${it.id}`} className={`border p-3 flex items-center gap-3 ${it.hidden ? "border-red-500/30 opacity-60" : "border-white/10"}`}>
              {it.kind === "video" && it.thumbnail ? (
                <img src={streamThumb(it.thumbnail)} alt="" className="w-20 h-12 object-cover shrink-0 bg-black/40" />
              ) : (
                <div className="w-20 h-12 shrink-0 bg-gradient-to-br from-[#A855F7]/20 to-[#14A89A]/10" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-body text-[10px] tracking-[0.15em] uppercase text-[#14A89A] truncate">{it.source_name}</div>
                <div className="font-heading text-sm text-white truncate">{it.title}</div>
              </div>
              <button onClick={() => toggleHide(it)} data-testid={`stream-item-hide-${it.id}`} title={it.hidden ? "Hidden: click to show" : "Visible: click to hide"} className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 font-body text-[10px] tracking-wider uppercase border transition-colors ${it.hidden ? "border-red-500/40 text-red-400" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                {it.hidden ? <><EyeOff size={13} /> Hidden</> : <><Eye size={13} /> Visible</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StreamManager = () => {
  const [section, setSection] = useState("sources");
  return (
    <div data-testid="stream-manager">
      <p className="font-body text-sm text-zinc-400 mb-6 leading-relaxed">Curate the Conscious Stream, the public feed of videos and articles. Sources are polled automatically every 6 hours; you can add channels, tag topics, filter noise, and hide individual items.</p>
      <div className="flex gap-6 border-b border-white/10 mb-8">
        <SubTab id="sources" label="Sources" active={section} onClick={setSection} />
        <SubTab id="filters" label="Filters" active={section} onClick={setSection} />
        <SubTab id="items" label="Items" active={section} onClick={setSection} />
      </div>
      {section === "sources" && <Sources />}
      {section === "filters" && <Filters />}
      {section === "items" && <Items />}
    </div>
  );
};
